import React from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { Shield, Users, Award, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

export const AboutPage = () => {
  const principles = [
    {
      icon: <Shield className="h-8 w-8 text-blue-600" />,
      title: "Risk-Based Approach",
      description: "We apply ICH Q9 principles to focus efforts where they matter most, ensuring efficient resource allocation and maximum compliance impact."
    },
    {
      icon: <Users className="h-8 w-8 text-green-600" />,
      title: "Practical Implementation",
      description: "Our solutions are designed for real-world application, creating systems that teams can actually use and maintain effectively."
    },
    {
      icon: <Award className="h-8 w-8 text-purple-600" />,
      title: "Audit-Ready Excellence",
      description: "Every deliverable is crafted with regulatory inspection readiness in mind, building confidence in your compliance posture."
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-orange-600" />,
      title: "Right-Sizing Over Gold-Plating",
      description: "We believe in lean, scalable governance that grows with your organization without unnecessary complexity."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-navy-900 mb-6">
              About <span className="text-blue-600">SP GxP Consultants</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              We are seasoned GMP practitioners who understand that compliance isn't just about 
              meeting regulations—it's about building sustainable systems that protect patients 
              and enable business success.
            </p>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-navy-900 mb-6">Our Story</h2>
              <div className="space-y-6 text-lg text-gray-700">
                <p>
                  SP GxP Consultants was founded on a simple principle: <strong>"Practical GMP. Predictable Outcomes."</strong> 
                  This philosophy emerged from our collective experience of seeing organizations struggle 
                  with over-engineered compliance systems that looked impressive on paper but failed in practice.
                </p>
                <p>
                  With over 36 years of combined leadership in GMP and GxP programs, our team has 
                  guided pharmaceutical and biotech companies through successful regulatory inspections, 
                  system implementations, and compliance transformations.
                </p>
                <p>
                  We believe in <strong>right-sizing over gold-plating</strong>—creating elegant, 
                  efficient solutions that your teams can actually use and maintain. Every system 
                  we design is built with inspection readiness and long-term sustainability in mind.
                </p>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1601839215170-6ce5854968d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxiaW90ZWNoJTIwY29tcGxpYW5jZXxlbnwwfHx8fDE3NTk5MjgwNjN8MA&ixlib=rb-4.1.0&q=85&w=600&h=400"
                alt="Pharmaceutical laboratory equipment demonstrating precision and compliance"
                className="w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Our Principles</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core principles guide every engagement and ensure consistent, 
              high-quality outcomes for our clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {principles.map((principle, index) => (
              <Card key={index} className="shadow-lg border-0 hover:shadow-xl transition-all duration-300">
                <CardHeader className="pb-4">
                  <div className="mb-4">
                    {principle.icon}
                  </div>
                  <CardTitle className="text-xl text-navy-900">
                    {principle.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed">
                    {principle.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Our Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the seasoned professional who brings decades of hands-on 
              experience in pharmaceutical quality and compliance.
            </p>
          </div>

          <div className="flex justify-center">
            <Card className="shadow-lg border-0 max-w-md">
              <CardHeader>
                <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-4 shadow-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_pharma-readiness/artifacts/glbipo02_dad%20prof%201.png"
                    alt="S.V.V Prasad Surampudi - Founder"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardTitle className="text-xl text-navy-900 text-center">S.V.V Prasad Surampudi</CardTitle>
                <p className="text-blue-600 font-semibold text-center">Founder</p>
              </CardHeader>
              <CardContent className="space-y-4 text-center">
                <p className="text-gray-600">36+ years of experience in Quality department of pharmaceutical industry</p>
                <div>
                  <h4 className="font-semibold text-navy-900 mb-2">Key Specialties:</h4>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {['Part 11 Compliance', 'Vendor Audits', 'Quality Systems', 'GMP Implementation'].map((specialty, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};